<template lang="pug">
    .map-box(v-if="token" :class="[spacingTop, spacingBottom]")
        MglMap(
            ref="mapbox"
            :class="{ show: visible }"
            :access-token="token"
            :map-style="style"
            :center="center"
            :pitch="pitch"
            :bearing="bearing"
            :zoom="zoom"
            :max-zoom="18"
            :min-zoom="3"
            :interactive="interactive"
            :style="{ height }"
            @load="loaded"
        )
            Pin(
                v-for="marker in markers" :key="marker.id"
                :marker="marker"
            )
</template>

<script>
import { debounce, get, isNumber } from '@/helpers';

import Mapbox from 'mapbox-gl';
import MultiTouch from 'mapbox-gl-multitouch';
import { MglMap } from 'vue-mapbox';

import { mapGetters } from 'vuex';

import Pin from './Pin';

export default {
    name: 'MapBox',
    props: {
        section: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        visible: false,
        group: undefined,
        flew: false,
        markers: []
    }),
    components: {
        MglMap,
        Pin
    },
    computed: {
        height() {
            let height = '400px';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'height');
                if (setting) height = setting.value;
                if (isNumber(height)) `${height}px`;
            }

            return height;
        },

        spacingTop() {
            let spacingTop = 'spacingTopStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingTop');
                if (setting && setting.value !== undefined) spacingTop = setting.value;
            }
            return spacingTop;
        },

        spacingBottom() {
            let spacingBottom = 'spacingBottomStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingBottom');
                if (setting && setting.value !== undefined) spacingBottom = setting.value;
            }
            return spacingBottom;
        },

        interactive() {
            // default interactive setting value is true
            let interactive = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'interactive');
                if (setting) interactive = ['true', '1', 'yes', true].includes(setting.value);
            }

            return interactive;
        },
        style() {
            let style = 'mapbox://styles/vanameland/ckirockk106341ao7dv35bc1p';

            if (this.section.settings && this.section.settings.length) {
                const styleSetting = this.section.settings.find(setting => setting.key === 'style');
                const customSetting = this.section.settings.find(setting => setting.key === 'mapbox-style-url');

                if (styleSetting && styleSetting.value && styleSetting.value.startsWith('mapbox://')) {
                    style = styleSetting.value;
                } else if (
                    styleSetting &&
                    styleSetting.value === 'custom' &&
                    customSetting &&
                    customSetting.value.startsWith('mapbox://')
                ) {
                    style = customSetting.value;
                }
            }

            return style;
        },
        autoZoom() {
            // default autoZoom setting value is true
            let autoZoom = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'auto-zoom-by-markers');
                if (setting) autoZoom = ['true', '1', 'yes', true].includes(setting.value);
            }

            return autoZoom;
        },
        zoom() {
            let zoom;
            if (this.autoZoom === false) zoom = 4;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'zoom');
                if (setting) zoom = parseFloat(setting.value);
            }

            return zoom;
        },
        pitch() {
            let pitch = 40;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'pitch');
                if (setting) pitch = parseFloat(setting.value);
            }

            return pitch;
        },
        bearing() {
            let bearing = -30;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'bearing');
                if (setting) bearing = parseFloat(setting.value);
            }

            return bearing;
        },
        autoCenter() {
            // default autoCenter setting value is true
            let autoCenter = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'auto-center-by-markers');
                if (setting) autoCenter = ['true', '1', 'yes', true].includes(setting.value);
            }

            return autoCenter;
        },
        latitude() {
            let latitude = 10.4515;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'latitude');
                if (setting) latitude = parseFloat(setting.value);
            }

            return latitude;
        },
        longitude() {
            let longitude = 51.1657;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'longitude');
                if (setting) longitude = parseFloat(setting.value);
            }

            return longitude;
        },
        center() {
            let center = [10.4515, 51.1657]; // germany

            if (this.autoCenter === false && this.latitude !== undefined && this.longitude !== undefined) {
                center = [this.latitude, this.longitude];
            } else if (this.markers.length && this.autoCenter) {
                let bounds = new Mapbox.LngLatBounds();
                this.markers.forEach(marker => bounds.extend(marker.coordinates));
                center = bounds.getCenter();
            }

            return center;
        },
        animate() {
            // default animate setting value is false
            let animate = false;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animate');
                if (setting) animate = ['true', '1', 'yes', true].includes(setting.value);
            }

            return animate;
        },
        duration() {
            // default duration setting value is 10000
            let duration = 10000;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'duration');
                if (setting) duration = parseInt(setting.value);
            }

            return duration;
        },
        animationAutoZoom() {
            // default autoZoom setting value is true
            let autoZoom = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animation-auto-zoom-by-markers');
                if (setting) autoZoom = ['true', '1', 'yes', true].includes(setting.value);
            }

            return autoZoom;
        },
        animationZoom() {
            let zoom;
            if (this.animationAutoZoom === false) zoom = 12;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animation-zoom');
                if (setting) zoom = parseFloat(setting.value);
            }

            return zoom;
        },
        animationPitch() {
            let pitch = 0;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animation-pitch');
                if (setting) pitch = parseFloat(setting.value);
            }

            return pitch;
        },
        animationBearing() {
            let bearing = 0;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animation-bearing');
                if (setting) bearing = parseFloat(setting.value);
            }

            return bearing;
        },
        animationAutoCenter() {
            // default autoCenter setting value is true
            let autoCenter = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(
                    setting => setting.key === 'animation-auto-center-by-markers'
                );
                if (setting) autoCenter = ['true', '1', 'yes', true].includes(setting.value);
            }

            return autoCenter;
        },
        animationLatitude() {
            let latitude = 10.4515;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animation-latitude');
                if (setting) latitude = parseFloat(setting.value);
            }

            return latitude;
        },
        animationLongitude() {
            let longitude = 51.1657;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'animation-longitude');
                if (setting) longitude = parseFloat(setting.value);
            }

            return longitude;
        },
        animationCenter() {
            let center = [10.4515, 51.1657]; // germany

            if (
                this.animationAutoCenter === false &&
                this.animationLatitude !== undefined &&
                this.animationLongitude !== undefined
            ) {
                center = [this.animationLatitude, this.animationLongitude];
            } else if (this.markers.length && this.animationAutoCenter) {
                let bounds = new Mapbox.LngLatBounds();
                this.markers.forEach(marker => bounds.extend(marker.coordinates));
                center = bounds.getCenter();
            }

            return center;
        },
        ...mapGetters({
            token: 'mapBoxToken'
        })
    },
    methods: {
        showMarkers() {
            if (!this.markers.length) return;

            const run = (cb, repeats, timeout = 100, i = 0) => {
                cb(i);
                setTimeout(() => {
                    ++i;
                    if (repeats >= i + 1) run(cb, repeats, timeout, i);
                }, timeout);
            };

            run(
                i => {
                    this.markers[i].visible = true;
                },
                this.markers.length,
                100
            );
        },
        flyToLocation(duration) {
            if (!this.$refs.mapbox) return;
            const map = this.$refs.mapbox.map;

            this.visible = true;

            let center = this.center;
            let pitch = this.pitch;
            let bearing = this.bearing;
            let zoom = this.zoom;

            let autoZoom = this.autoZoom;
            let autoCenter = this.autoCenter;

            if (this.animate) {
                center = this.animationCenter;
                pitch = this.animationPitch;
                bearing = this.animationBearing;
                zoom = this.animationZoom;

                autoZoom = this.animationAutoZoom;
                autoCenter = this.animationAutoCenter;
            }

            if (this.markers.length && autoZoom && autoCenter) {
                let bounds = new Mapbox.LngLatBounds();
                this.markers.forEach(marker => bounds.extend(marker.coordinates));

                map.fitBounds(
                    bounds,
                    {
                        padding: 40,
                        bearing,
                        pitch,
                        maxZoom: 10,
                        duration
                    },
                    {
                        breakonfitbounds: true
                    }
                );

                setTimeout(this.showMarkers, duration / 2);
            } else {
                map.flyTo(
                    {
                        center,
                        essential: true,
                        bearing,
                        pitch,
                        zoom: zoom || 3.5,
                        duration
                    },
                    {
                        breakonfitbounds: true
                    }
                );
            }

            this.flew = true;
        },
        observeVisibility() {
            const checkIfItsTimeToFly = () => {
                if (!this.$refs.mapbox) return;

                // check if map is visible
                const rect = this.$refs.mapbox.$el.getBoundingClientRect();
                const itsTimeToFly = rect.top < window.innerHeight;

                if (!this.flew && itsTimeToFly) {
                    let duration = 0;
                    // override duration if animation setting is true
                    if (this.animate) duration = this.duration;

                    this.flyToLocation(duration);
                }
            };

            window.addEventListener(
                'scroll',
                debounce(() => checkIfItsTimeToFly(), 100)
            );
            window.addEventListener('touchstart', () => checkIfItsTimeToFly(5));
            checkIfItsTimeToFly();
        },
        async loaded(event) {
            let map = event.map;

            map.dragRotate.disable();

            let timeout,
                reset = false;
            map.on('moveend', event => {
                if (timeout) clearTimeout(timeout);
                if (event.breakonfitbounds || reset) {
                    reset = true;
                    timeout = setTimeout(() => {
                        map.easeTo({ pitch: 0, bearing: 0 });
                        reset = false;

                        this.showMarkers();
                        this.flew = true;
                    }, 100);
                }
            });
            map.on('move', () => {
                if (timeout) clearTimeout(timeout);
            });

            map.addControl(new MultiTouch());

            setTimeout(this.observeVisibility, 300);
        }
    },
    mounted() {
        const contents = this.section.contents || [];
        contents
            .filter(el => el.key === 'marker' && !el.deleted)
            .forEach(content => {
                const address = get(content, 'addresses.0');
                if (address && !address.deleted && address.latitude && address.longitude) {
                    this.markers.push({
                        id: content.id,
                        coordinates: [address.latitude, address.longitude],
                        color: content.color,
                        title: content.title,
                        description: content.text,
                        hyperlink: content.hyperlink,
                        settings: content.settings,
                        visible: false
                    });
                }
            });

        window.addEventListener(
            'resize',
            debounce(() => {
                if (this.flew) this.flyToLocation(1000);
            }, 100)
        );
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.map-box
  @apply bg-black


.mgl-map-wrapper
    opacity: 0
    transition: opacity .3s ease
    width: 100%
    min-height: 245px
    box-size: border-box
    max-height: calc(100vh - 2rem)
    &.show
        opacity: 1
    ::v-deep
        .mapboxgl-canvas-container
            height: 100%
            width: 100%
.groups-filter
    padding: 1rem
    background: gray
    display: flex
    justify-content: center
    align-items: center
    .button
        margin: .5rem
    .title
        font-size: 1rem
        color: #fff
        margin: .5rem
        font-weight: normal
</style>
